import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { HealthMatrix } from "@/features/health";
import { apiGet } from "@/lib/api-client";
import { asDateTime, formatRelativeTime } from "@/lib/format";
import { asRecord } from "@/lib/type-utils";

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return "Unknown";
  }
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
}

type CollectorTone = "green" | "yellow" | "red" | "grey";

const COLLECTOR_TONE_CLASSES: Record<CollectorTone, { text: string; dot: string }> = {
  green: { text: "text-green-400", dot: "bg-green-400" },
  yellow: { text: "text-yellow-400", dot: "bg-yellow-400" },
  red: { text: "text-red-400", dot: "bg-red-400" },
  grey: { text: "text-slate-400", dot: "bg-slate-500" },
};

function CollectorStatusRow({ label, tone, status }: { label: string; tone: CollectorTone; status: string }) {
  const classes = COLLECTOR_TONE_CLASSES[tone];
  return (
    <div className="flex items-center justify-between rounded-lg bg-gunmetal/50 p-3">
      <span className="text-sm">{label}</span>
      <span className={`flex items-center gap-2 text-sm ${classes.text}`}>
        <span className={`h-2 w-2 rounded-full ${classes.dot}`} />
        {status}
      </span>
    </div>
  );
}

interface BrainStatus {
  cycle_count: number;
  total_trades_placed: number;
  errors_last_cycle: number;
  last_cycle_at: string | null;
  started_at: string | null;
  is_alive: boolean;
  // Intent breakdown — null when migration 044 has not yet run
  yes_intents_last_cycle: number | null;
  no_intents_last_cycle: number | null;
  exit_intents_last_cycle: number | null;
  total_yes_intents: number | null;
  total_no_intents: number | null;
  total_exit_intents: number | null;
  // Scan coverage — available after migration 047
  markets_scanned_last_cycle: number | null;
  markets_analyzed_last_cycle: number | null;
}

interface TradeDecision {
  timestamp: string;
  intent_summary: string;
  outcome: string;
  edge_net: number;
  decision: string;
  reason: string;
}

interface TradeAuditResponse {
  total_logged: number;
  returned: number;
  source: string;
  records: TradeDecision[];
}

interface ReconciliationRun {
  id: number;
  run_at: string | null;
  ok: boolean;
  halt_recommended: boolean;
  internal_count: number;
  clob_fill_token_count: number;
  matched_count: number;
  discrepancy_count: number;
  error_message: string | null;
}

interface ReconciliationHistory {
  runs: ReconciliationRun[];
  latest_ok: boolean | null;
  halt_recommended: boolean;
  total_returned: number;
  error?: string;
}

function ReconciliationPanel({ query }: { query: UseQueryResult<ReconciliationHistory> }) {
  const data = query.data;
  const latestOk = data?.latest_ok;
  const haltRecommended = data?.halt_recommended ?? false;
  const runs = data?.runs ?? [];

  const statusColor =
    haltRecommended ? "text-red-400" :
    latestOk === true ? "text-green-400" :
    latestOk === false ? "text-yellow-400" :
    "text-slate-400";

  const statusLabel =
    haltRecommended ? "HALT" :
    latestOk === true ? "OK" :
    latestOk === false ? "FAIL" :
    query.isLoading ? "LOADING…" : "NO DATA";

  const dotColor =
    haltRecommended ? "bg-red-400" :
    latestOk === true ? "bg-green-400" :
    latestOk === false ? "bg-yellow-400" :
    "bg-slate-500";

  return (
    <Panel
      title="CLOB Reconciliation"
      description="Recent CLOB position audit history — checks internal fills against live CLOB state."
      actions={
        <span className="flex items-center gap-2 text-sm">
          <span className={`h-2 w-2 rounded-full ${dotColor}`} />
          <span className={statusColor}>{statusLabel}</span>
        </span>
      }
    >
      {runs.length === 0 ? (
        <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
          {query.isLoading ? "Loading reconciliation history…" : "No reconciliation runs recorded yet."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs md:text-sm">
            <thead>
              <tr className="border-b border-slate-700 text-left text-slate-400">
                <th className="pb-2 pr-4 font-medium">Run At</th>
                <th className="pb-2 pr-4 font-medium text-center">Status</th>
                <th className="pb-2 pr-4 font-medium text-right">Internal</th>
                <th className="pb-2 pr-4 font-medium text-right">CLOB</th>
                <th className="pb-2 pr-4 font-medium text-right">Matched</th>
                <th className="pb-2 font-medium text-right">Gaps</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {runs.map((run) => (
                <tr key={run.id} className="hover:bg-gunmetal/30">
                  <td className="py-2 pr-4 text-slate-300 whitespace-nowrap">
                    {asDateTime(run.run_at)}
                  </td>
                  <td className="py-2 pr-4 text-center">
                    {run.halt_recommended ? (
                      <span className="rounded px-1.5 py-0.5 text-xs font-semibold bg-red-900/60 text-red-300">HALT</span>
                    ) : run.ok ? (
                      <span className="rounded px-1.5 py-0.5 text-xs font-semibold bg-green-900/60 text-green-300">OK</span>
                    ) : (
                      <span className="rounded px-1.5 py-0.5 text-xs font-semibold bg-yellow-900/60 text-yellow-300">FAIL</span>
                    )}
                    {run.error_message && (
                      <div className="mt-0.5 text-[0.6rem] text-red-400 truncate max-w-[10rem]" title={run.error_message}>
                        {run.error_message}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right text-slate-300">{run.internal_count}</td>
                  <td className="py-2 pr-4 text-right text-slate-300">{run.clob_fill_token_count}</td>
                  <td className="py-2 pr-4 text-right text-slate-300">{run.matched_count}</td>
                  <td className={`py-2 text-right font-medium ${run.discrepancy_count > 0 ? "text-red-400" : "text-green-400"}`}>
                    {run.discrepancy_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

export function OpsPulsePage() {
  const healthQuery = useQuery({
    queryKey: ["ops-pulse", "health"],
    queryFn: () => apiGet<unknown>("/api/health/deep"),
    refetchInterval: 10_000,
  });

  const ingestionQuery = useQuery({
    queryKey: ["ops-pulse", "ingestion"],
    queryFn: () => apiGet<unknown>("/api/collect/status"),
    refetchInterval: 15_000,
  });

  const dbStatsQuery = useQuery({
    queryKey: ["ops-pulse", "db-stats"],
    queryFn: () => apiGet<unknown>("/api/historical/stats"),
    refetchInterval: 60_000,
  });

  const pipelineQuery = useQuery({
    queryKey: ["ops-pulse", "pipeline"],
    queryFn: () => apiGet<unknown>("/api/signals/health"),
    refetchInterval: 15_000,
  });

  const brainStatusQuery = useQuery({
    queryKey: ["ops-pulse", "brain-status"],
    queryFn: () => apiGet<BrainStatus>("/api/brain/status"),
    refetchInterval: 30_000,
  });

  const reconciliationQuery = useQuery({
    queryKey: ["ops-pulse", "reconciliation"],
    queryFn: () => apiGet<ReconciliationHistory>("/api/brain/reconciliation?limit=10"),
    refetchInterval: 60_000,
  });

  const tradeAuditQuery = useQuery({
    queryKey: ["ops-pulse", "trade-audit"],
    queryFn: () => apiGet<TradeAuditResponse>("/api/trades/audit?limit=10"),
    refetchInterval: 30_000,
  });

  const newsSentinelQuery = useQuery({
    queryKey: ["ops-pulse", "news-sentinel"],
    queryFn: () => apiGet<unknown>("/api/news/sentinel/status"),
    refetchInterval: 60_000,
  });

  const resolutionStatusQuery = useQuery({
    queryKey: ["ops-pulse", "resolution-status"],
    queryFn: () => apiGet<unknown>("/api/resolution/status"),
    refetchInterval: 60_000,
  });

  const allQueries = [
    healthQuery,
    ingestionQuery,
    dbStatsQuery,
    pipelineQuery,
    brainStatusQuery,
    reconciliationQuery,
    tradeAuditQuery,
    newsSentinelQuery,
    resolutionStatusQuery,
  ];
  const anyError = allQueries.some((q) => q.isError);

  const health = asRecord(healthQuery.data);
  const checks = asRecord(health.checks);
  const ingestion = asRecord(ingestionQuery.data);
  const dbStats = asRecord(dbStatsQuery.data);
  const pipeline = asRecord(pipelineQuery.data);

  const dbLatency = Number((checks.database as Record<string, number>)?.latency_ms ?? 0);
  const redisLatency = Number((checks.redis as Record<string, number>)?.latency_ms ?? 0);
  const overallStatus = healthQuery.isError ? "unreachable" : String(health.status ?? "unknown");

  // /api/collect/status returns HTTP 200 with {success:false, ...} on internal
  // failure — treat that exactly like a failed request, never as healthy data.
  const collectDegraded =
    ingestionQuery.isError || (ingestionQuery.isSuccess && ingestion.success === false);
  const collectReady = ingestionQuery.isSuccess && !collectDegraded;
  const tablesInitialized = Boolean(ingestion.tables_initialized);
  const trades24h = Number(ingestion.trades_last_24h ?? 0);

  // Market-data health = is trade data actually FLOWING, measured by the
  // deep-health ingestion lag. The collector's websocket_connected flag
  // tracks the API-process's own (unused) WS client and reads "false" while
  // the external collector service ingests millions of rows — keying status
  // off it showed "Down" next to "5.4M trades in 24h" (prod, 2026-06-12).
  const tradesIngestionCheck = asRecord(checks.trades_ingestion);
  // lag_seconds is explicitly null (not absent) when the trades table has no
  // MAX(time) — and Number(null) is 0, which must not read as a fresh 0.0s
  // lag. Null/undefined mean MISSING telemetry.
  const rawIngestLag = tradesIngestionCheck.lag_seconds;
  const ingestLagSec = rawIngestLag == null ? Number.NaN : Number(rawIngestLag);
  const ingestionVerdict = String(tradesIngestionCheck.status ?? "");
  // Without lag telemetry, trades_last_24h is a 24h AGGREGATE — it can stay
  // positive for hours after ingestion stalls, so it must never prove
  // "Flowing". Zero in 24h does prove nothing flowed; positive only means
  // freshness is unknown. An explicit backend "stale" verdict wins outright.
  const marketDataFlow: "flowing" | "stalled" | "unknown" =
    ingestionVerdict === "stale"
      ? "stalled"
      : Number.isFinite(ingestLagSec)
        ? ingestLagSec < 300
          ? "flowing"
          : "stalled"
        : trades24h > 0
          ? "unknown"
          : "stalled";

  const totalTrades = Number(dbStats.trades ?? 0);
  const totalPrices = Number(dbStats.prices ?? dbStats.price_points ?? 0);
  const totalMarkets = Number(dbStats.markets ?? 0);
  const sizeBytes = Number(dbStats.size_bytes);
  const dbSize = dbStatsQuery.isError
    ? "—"
    : typeof dbStats.size === "string"
      ? dbStats.size
      : Number.isFinite(sizeBytes)
        ? formatBytes(sizeBytes)
        : "Unknown";

  const pipelineStatus = String(pipeline.status ?? "unknown");
  const pipelineLag = Number(pipeline.lag_seconds ?? 0);
  const tradesPerMin = Number(pipeline.trades_last_min ?? 0);
  const activeMarkets = Number(pipeline.active_markets ?? 0);

  // Collector Status rows — bound to real telemetry, never hardcoded green.
  const sentinel = asRecord(asRecord(newsSentinelQuery.data).sentinel);
  const newsRow: { tone: CollectorTone; status: string } = newsSentinelQuery.isError
    ? { tone: "red", status: "Unavailable" }
    : !newsSentinelQuery.isSuccess
      ? { tone: "grey", status: "—" }
      : sentinel.last_scan
        ? { tone: "green", status: "Active" }
        : { tone: "yellow", status: "Idle — no scan yet" };

  const priceFeedRow: { tone: CollectorTone; status: string } = !collectReady
    ? { tone: collectDegraded ? "red" : "grey", status: collectDegraded ? "Unknown" : "—" }
    : !tablesInitialized
      ? { tone: "yellow", status: "Degraded" }
      : marketDataFlow === "flowing"
        ? { tone: "green", status: "Active (collector service)" }
        : marketDataFlow === "unknown"
          ? { tone: "yellow", status: "Unknown — no freshness telemetry" }
          : { tone: "red", status: "Stalled — no fresh ticks" };

  const tradeIngestRow: { tone: CollectorTone; status: string } = !collectReady
    ? { tone: collectDegraded ? "red" : "grey", status: collectDegraded ? "Unknown" : "—" }
    : !tablesInitialized
      ? { tone: "yellow", status: "Degraded" }
      : trades24h > 0
        ? { tone: "green", status: `Active · ${trades24h.toLocaleString()} in 24h` }
        : { tone: "yellow", status: "No trades in 24h" };

  const resolutionPayload = asRecord(resolutionStatusQuery.data);
  // /api/resolution/status also reports failure as 200 + {error: ...}.
  const resolutionRow: { tone: CollectorTone; status: string } = resolutionStatusQuery.isError
    ? { tone: "red", status: "Unavailable" }
    : !resolutionStatusQuery.isSuccess
      ? { tone: "grey", status: "—" }
      : typeof resolutionPayload.error === "string" && resolutionPayload.error
        ? { tone: "red", status: "Unavailable" }
        : { tone: "green", status: "Active" };

  const brainData = brainStatusQuery.data;
  // The backend's is_alive uses a 10-minute window, but the brain only stamps
  // last_cycle_at when a FULL cycle completes and a real multi-market cycle
  // takes 40-90 minutes — so a healthy brain read "STALE" for most of every
  // cycle (prod, 2026-06-12). Derive freshness over a window that covers the
  // slowest observed cycle; the backend threshold fix is flagged for the
  // protected lane (backend/routers/brain.py is guard-blocked).
  const BRAIN_FRESH_WINDOW_MS = 120 * 60 * 1000;
  const brainLastCycleMs = brainData?.last_cycle_at
    ? new Date(String(brainData.last_cycle_at)).getTime()
    : Number.NaN;
  const brainIsAlive =
    Boolean(brainData?.is_alive) ||
    (Number.isFinite(brainLastCycleMs) &&
      Date.now() - brainLastCycleMs < BRAIN_FRESH_WINDOW_MS);
  const brainCycleCount = Number(brainData?.cycle_count ?? 0);
  const brainTotalTrades = Number(brainData?.total_trades_placed ?? 0);
  const brainErrorsLast = Number(brainData?.errors_last_cycle ?? 0);
  const brainLastCycleAt = brainData?.last_cycle_at ?? null;
  const brainStartedAt = brainData?.started_at ?? null;
  // Intent breakdown — available after migration 044
  const brainYesLast = brainData?.yes_intents_last_cycle != null ? Number(brainData.yes_intents_last_cycle) : null;
  const brainNoLast = brainData?.no_intents_last_cycle != null ? Number(brainData.no_intents_last_cycle) : null;
  const brainExitLast = brainData?.exit_intents_last_cycle != null ? Number(brainData.exit_intents_last_cycle) : null;
  const brainTotalYes = brainData?.total_yes_intents != null ? Number(brainData.total_yes_intents) : null;
  const brainTotalNo = brainData?.total_no_intents != null ? Number(brainData.total_no_intents) : null;
  const brainTotalExit = brainData?.total_exit_intents != null ? Number(brainData.total_exit_intents) : null;
  const hasIntentBreakdown = brainYesLast !== null && brainNoLast !== null && brainExitLast !== null;
  // Scan coverage — available after migration 047
  const brainScanned = brainData?.markets_scanned_last_cycle != null ? Number(brainData.markets_scanned_last_cycle) : null;
  const brainAnalyzed = brainData?.markets_analyzed_last_cycle != null ? Number(brainData.markets_analyzed_last_cycle) : null;
  const hasScanMetrics = brainScanned !== null && brainAnalyzed !== null;

  const getStatusTone = (status: string) => {
    if (status === "healthy") return "success" as const;
    if (status === "degraded") return "warning" as const;
    return "default" as const;
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {anyError ? (
        <QueryErrorState
          what="operations telemetry"
          onRetry={() => {
            for (const q of allQueries) {
              if (q.isError) void q.refetch();
            }
          }}
        />
      ) : null}

      <Panel
        title="Operations Pulse"
        description="Live system health, ingestion pipeline, and database metrics."
        actions={
          <span className="flex items-center gap-2 text-sm">
            <span
              className={`h-2 w-2 rounded-full ${
                overallStatus === "healthy" ? "bg-green-400" : overallStatus === "degraded" ? "bg-yellow-400" : "bg-red-400"
              }`}
            />
            <span className={overallStatus === "healthy" ? "text-green-400" : overallStatus === "degraded" ? "text-yellow-400" : "text-red-400"}>
              {overallStatus.toUpperCase()}
            </span>
          </span>
        }
      >
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
          <StatCard label="Database Latency" value={healthQuery.isSuccess ? `${dbLatency.toFixed(0)}ms` : "—"} tone={healthQuery.isSuccess && dbLatency < 100 ? "success" : healthQuery.isSuccess && dbLatency < 500 ? "warning" : "default"} hint="Query response time" />
          <StatCard label="Redis Latency" value={healthQuery.isSuccess ? `${redisLatency.toFixed(0)}ms` : "—"} tone={healthQuery.isSuccess && redisLatency < 50 ? "success" : healthQuery.isSuccess && redisLatency < 200 ? "warning" : "default"} hint="Cache response time" />
          <StatCard
            label="Market Data Flow"
            value={
              !collectReady
                ? "—"
                : marketDataFlow === "flowing"
                  ? "Flowing"
                  : marketDataFlow === "unknown"
                    ? "Unknown"
                    : "Stalled"
            }
            tone={
              !collectReady
                ? "default"
                : marketDataFlow === "flowing"
                  ? "success"
                  : marketDataFlow === "unknown"
                    ? "warning"
                    : "danger"
            }
            hint={
              Number.isFinite(ingestLagSec)
                ? `Trade-stream lag ${ingestLagSec.toFixed(1)}s (collector service)`
                : "No freshness telemetry (deep-health lag unavailable)"
            }
          />
          <StatCard label="Trades Ingested (24h)" value={collectReady ? trades24h.toLocaleString() : "—"} tone={collectReady && trades24h > 0 ? "success" : "default"} hint="Trades captured in the last 24h" />
          <StatCard label="Pipeline Status" value={pipelineStatus.toUpperCase()} tone={getStatusTone(pipelineStatus)} hint="Signal pipeline health" />
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Database Footprint" description="Storage and data volume metrics">
          <div className="grid grid-cols-2 gap-2 md:gap-4">
            <div className="rounded-lg bg-gunmetal/50 p-3 md:p-4 text-center">
              <div className="text-lg md:text-2xl font-bold text-gold-400">{dbStatsQuery.isSuccess ? totalTrades.toLocaleString() : "—"}</div>
              <div className="text-[0.65rem] md:text-xs text-slate-400">Total Trades</div>
            </div>
            <div className="rounded-lg bg-gunmetal/50 p-3 md:p-4 text-center">
              <div className="text-lg md:text-2xl font-bold text-gold-400">{dbStatsQuery.isSuccess ? totalPrices.toLocaleString() : "—"}</div>
              <div className="text-[0.65rem] md:text-xs text-slate-400">Price Points</div>
            </div>
            <div className="rounded-lg bg-gunmetal/50 p-3 md:p-4 text-center">
              <div className="text-lg md:text-2xl font-bold text-gold-400">{dbStatsQuery.isSuccess ? totalMarkets.toLocaleString() : "—"}</div>
              <div className="text-[0.65rem] md:text-xs text-slate-400">Markets Tracked</div>
            </div>
            <div className="rounded-lg bg-gunmetal/50 p-3 md:p-4 text-center">
              <div className="text-base md:text-lg font-bold text-cyan-400">{dbSize}</div>
              <div className="text-[0.65rem] md:text-xs text-slate-400">Database Size</div>
            </div>
          </div>
        </Panel>

        <Panel title="Signal Pipeline" description="Real-time signal processing metrics">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Status</span>
              <span className={`text-sm font-medium ${pipelineStatus === "healthy" ? "text-green-400" : pipelineStatus === "degraded" ? "text-yellow-400" : "text-red-400"}`}>
                {pipelineStatus.toUpperCase()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Processing Lag</span>
              <span className={`text-sm font-medium ${!pipelineQuery.isSuccess ? "text-slate-400" : pipelineLag < 5 ? "text-green-400" : pipelineLag < 30 ? "text-yellow-400" : "text-red-400"}`}>
                {pipelineQuery.isSuccess ? `${pipelineLag.toFixed(1)}s` : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Trades/Minute</span>
              <span className="text-sm font-medium text-gold-400">{pipelineQuery.isSuccess ? tradesPerMin : "—"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Active Markets</span>
              <span className="text-sm font-medium text-gold-400">{pipelineQuery.isSuccess ? activeMarkets : "—"}</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-slate-400">
                <span>Pipeline Load</span>
                <span>{Math.min((tradesPerMin / 100) * 100, 100).toFixed(0)}%</span>
              </div>
              <div className="h-2 rounded-full bg-gunmetal">
                <div
                  className={`h-full rounded-full transition-all ${
                    tradesPerMin > 50 ? "bg-green-500" : tradesPerMin > 10 ? "bg-yellow-500" : "bg-red-500"
                  }`}
                  style={{ width: `${Math.min((tradesPerMin / 100) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </Panel>
      </div>

      <Panel
        title="Brain Status"
        description="Analysis engine health — ECS Frankfurt. Cycles run back-to-back; a full multi-market cycle takes 40–90 minutes."
        actions={
          <span className="flex items-center gap-2 text-sm">
            <span className={`h-2 w-2 rounded-full ${brainIsAlive ? "bg-green-400 animate-pulse" : "bg-red-400"}`} />
            <span className={brainIsAlive ? "text-green-400" : "text-red-400"}>
              {brainIsAlive ? "ALIVE" : brainStatusQuery.isLoading ? "LOADING…" : brainStatusQuery.isError ? "UNKNOWN" : "STALE"}
            </span>
          </span>
        }
      >
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
          <StatCard label="Cycles Completed" value={brainCycleCount.toLocaleString()} tone={brainCycleCount > 0 ? "success" : "default"} hint="Total analysis cycles since last restart" />
          <StatCard label="Intents Written" value={brainTotalTrades.toLocaleString()} hint="Cumulative TradeIntents queued" />
          <StatCard label="Errors (Last Cycle)" value={String(brainErrorsLast)} tone={brainErrorsLast === 0 ? "success" : brainErrorsLast < 3 ? "warning" : "danger"} hint="Errors in most recent cycle" />
          <StatCard label="Last Cycle" value={formatRelativeTime(brainLastCycleAt)} tone={brainIsAlive ? "success" : "default"} hint={brainLastCycleAt ?? "No cycle yet"} />
          <StatCard
            label="Brain Started"
            value={formatRelativeTime(brainStartedAt)}
            hint={
              brainStartedAt
                ? `Recorded start ${brainStartedAt} — may predate the current container`
                : "Not started"
            }
          />
          {hasScanMetrics && (
            <>
              <StatCard label="Markets Scanned" value={(brainScanned ?? 0).toLocaleString()} tone={(brainScanned ?? 0) > 0 ? "success" : "warning"} hint="Markets fetched from Gamma API last cycle" />
              <StatCard label="Markets Analyzed" value={(brainAnalyzed ?? 0).toLocaleString()} tone={(brainAnalyzed ?? 0) > 0 ? "success" : "warning"} hint="Markets that passed filters and entered the pipeline" />
            </>
          )}
        </div>
        {hasIntentBreakdown && (
          <div className="mt-4 border-t border-slate-700 pt-4">
            <div className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">Last Cycle Intent Breakdown</div>
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
                <div className="text-lg font-bold text-green-400">{(brainYesLast ?? 0).toLocaleString()}</div>
                <div className="text-[0.65rem] text-slate-400">YES</div>
                <div className="mt-1 text-[0.6rem] text-slate-500">cumul. {(brainTotalYes ?? 0).toLocaleString()}</div>
              </div>
              <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
                <div className="text-lg font-bold text-red-400">{(brainNoLast ?? 0).toLocaleString()}</div>
                <div className="text-[0.65rem] text-slate-400">NO</div>
                <div className="mt-1 text-[0.6rem] text-slate-500">cumul. {(brainTotalNo ?? 0).toLocaleString()}</div>
              </div>
              <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
                <div className="text-lg font-bold text-yellow-400">{(brainExitLast ?? 0).toLocaleString()}</div>
                <div className="text-[0.65rem] text-slate-400">EXIT</div>
                <div className="mt-1 text-[0.6rem] text-slate-500">cumul. {(brainTotalExit ?? 0).toLocaleString()}</div>
              </div>
            </div>
          </div>
        )}
      </Panel>

      <ReconciliationPanel query={reconciliationQuery} />

      <Panel
        title="Recent Trade Decisions"
        description={`Last 10 brain decisions · source: ${tradeAuditQuery.data?.source ?? "—"} · total: ${tradeAuditQuery.data?.total_logged?.toLocaleString() ?? "—"}`}
      >
        {tradeAuditQuery.isLoading ? (
          <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
            Loading decision records...
          </div>
        ) : (tradeAuditQuery.data?.records ?? []).length === 0 ? (
          <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
            No decision records yet
          </div>
        ) : (
          <div className="space-y-1.5">
            {(tradeAuditQuery.data?.records ?? []).map((rec, i) => {
              const isPlaced = rec.decision === "placed";
              const isRejected = rec.decision === "rejected" || rec.decision === "error";
              const edgeColor = isPlaced ? "text-green-400" : isRejected ? "text-red-400" : "text-slate-400";
              const dotColor = isPlaced ? "bg-green-400" : isRejected ? "bg-red-400" : "bg-slate-500";
              const edgePct = (rec.edge_net * 100).toFixed(1);
              return (
                <div key={i} className="flex items-start justify-between rounded-lg bg-gunmetal/50 px-3 py-2 gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${dotColor}`} />
                    <div className="min-w-0">
                      <div className="truncate text-xs text-slate-200">{rec.intent_summary || "—"}</div>
                      <div className="text-[0.6rem] text-slate-500">
                        {rec.timestamp ? formatRelativeTime(rec.timestamp) : "—"}
                        {rec.reason ? ` · ${rec.reason}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className={`flex-shrink-0 text-right text-xs font-mono ${edgeColor}`}>
                    <div>{rec.outcome || rec.decision}</div>
                    <div className="text-[0.65rem] text-slate-500">{edgePct}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Collector Status" description="Data ingestion pipeline health">
          <div className="space-y-3">
            <CollectorStatusRow label="News Ingestion" tone={newsRow.tone} status={newsRow.status} />
            <CollectorStatusRow label="Price Feeds" tone={priceFeedRow.tone} status={priceFeedRow.status} />
            <CollectorStatusRow label="Trade Ingestion" tone={tradeIngestRow.tone} status={tradeIngestRow.status} />
            {/* The collector subscribes trades+crypto only — there is no order
                book sync signal to bind, so say so instead of a fake green. */}
            <CollectorStatusRow label="Order Book Sync" tone="grey" status="No telemetry" />
            <CollectorStatusRow label="Resolution Monitor" tone={resolutionRow.tone} status={resolutionRow.status} />
          </div>
        </Panel>

        <Panel title="System Services" description="Backend service health">
          <div className="space-y-3">
            {Object.entries(checks).map(([name, check]) => {
              const checkData = asRecord(check);
              const status = String(checkData.status ?? "unknown");
              const rawLatency = checkData.latency_ms;
              const latencyMs =
                rawLatency != null && (typeof rawLatency === "number" || typeof rawLatency === "string")
                  ? String(Number(rawLatency).toFixed(0))
                  : null;
              return (
                <div key={name} className="flex items-center justify-between rounded-lg bg-gunmetal/50 p-3">
                  <div>
                    <span className="text-sm font-medium capitalize">{name.replace(/_/g, " ")}</span>
                    {latencyMs && (
                      <div className="text-xs text-slate-400">{latencyMs}ms</div>
                    )}
                  </div>
                  {/* Deep-health checks report "healthy" (database) OR "ok"
                      (trades_ingestion, redis) for the good state — both are
                      green; "ok" rendered red on the live dashboard. */}
                  <span className={`flex items-center gap-2 text-sm ${status === "healthy" || status === "ok" ? "text-green-400" : status === "degraded" ? "text-yellow-400" : "text-red-400"}`}>
                    <span className={`h-2 w-2 rounded-full ${status === "healthy" || status === "ok" ? "bg-green-400" : status === "degraded" ? "bg-yellow-400" : "bg-red-400"}`} />
                    {status}
                  </span>
                </div>
              );
            })}
            {Object.keys(checks).length === 0 && (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                No service health data available
              </div>
            )}
          </div>
        </Panel>

        {/* Unauthenticated load-balancer /health view — complements the
            authenticated /api/health/deep feeding System Services above. */}
        <HealthMatrix />
      </div>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Health API" description="Deep health check data">
          <JsonView value={healthQuery.data} />
        </Panel>
        <Panel title="Ingestion API" description="Collector status data">
          <JsonView value={ingestionQuery.data} />
        </Panel>
      </div>
    </div>
  );
}
